// Helpers for parsing the PDF template JSON spec and the sample-variable
// record from textarea strings. The PDF spec is a @json-render spec — a flat
// `{ root, elements }` graph (NOT a nested {type, children} tree) whose root
// is a Document > Page subtree. The backend renderer parses it with the same
// `{ root, elements }` contract, so we validate that shape here before preview
// or save to avoid persisting specs the renderer cannot consume.

export const EMPTY_PDF_JSON_SPEC = `{
  "root": "document",
  "elements": {
    "document": { "type": "Document", "props": {}, "children": ["page"] },
    "page": {
      "type": "Page",
      "props": { "size": "LETTER", "orientation": "portrait" },
      "children": ["body"]
    },
    "body": { "type": "View", "props": { "padding": 32 }, "children": ["heading"] },
    "heading": {
      "type": "Heading",
      "props": { "text": "New PDF template", "level": "h1" },
      "children": []
    }
  }
}`;

interface ParseResult<T> {
  data: T | null;
  error: string | null;
}

function parseJsonObject(value: string): ParseResult<Record<string, unknown>> {
  if (!value.trim()) {
    return { data: null, error: 'JSON is empty.' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err.message : 'Invalid JSON.',
    };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { data: null, error: 'JSON spec must be an object.' };
  }
  return { data: parsed as Record<string, unknown>, error: null };
}

export function parsePdfSpec(
  value: string,
): ParseResult<Record<string, unknown>> {
  const result = parseJsonObject(value);
  if (!result.data) return result;
  if (typeof result.data.root !== 'string' || !result.data.root) {
    return {
      data: null,
      error: 'JSON spec must have a string "root" element id.',
    };
  }
  const elements = result.data.elements;
  if (
    elements === null ||
    typeof elements !== 'object' ||
    Array.isArray(elements)
  ) {
    return {
      data: null,
      error: 'JSON spec must have an "elements" object keyed by element id.',
    };
  }
  return result;
}

export function parseJsonRecord(
  value: string,
): ParseResult<Record<string, unknown>> {
  return parseJsonObject(value);
}

interface SpecElement {
  type?: unknown;
  props?: unknown;
  children?: unknown;
}

function isSpecElement(value: unknown): value is SpecElement {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Locates the `Page` element id in a `{ root, elements }` spec: the root itself
 * if it is a Page, otherwise the first Page among the root Document's children,
 * otherwise the first Page anywhere. Returns null when there is no Page.
 */
function findPageElementId(
  root: string,
  elements: Record<string, unknown>,
): string | null {
  const rootEl = elements[root];
  if (isSpecElement(rootEl) && rootEl.type === 'Page') return root;
  if (isSpecElement(rootEl) && Array.isArray(rootEl.children)) {
    for (const childId of rootEl.children) {
      const child = typeof childId === 'string' ? elements[childId] : undefined;
      if (isSpecElement(child) && child.type === 'Page') return childId;
    }
  }
  for (const [id, el] of Object.entries(elements)) {
    if (isSpecElement(el) && el.type === 'Page') return id;
  }
  return null;
}

/**
 * Page setup (size/orientation/margins) is owned by the form controls, but the
 * renderer reads it from the spec's `Page` node. This merges the form's page
 * setup into that node so the controls actually drive both the live preview and
 * the persisted/rendered output. Only defined values are applied; unspecified
 * margins are left off so the renderer's own default (40pt) applies.
 */
export function applyPageSetupToSpec(
  spec: Record<string, unknown>,
  pageSetup: {
    size?: string;
    orientation?: string;
    marginTop?: number;
    marginBottom?: number;
    marginLeft?: number;
    marginRight?: number;
  },
): Record<string, unknown> {
  const root = spec.root;
  const elements = spec.elements;
  if (
    typeof root !== 'string' ||
    elements === null ||
    typeof elements !== 'object' ||
    Array.isArray(elements)
  ) {
    return spec;
  }
  const elementRecord = elements as Record<string, unknown>;
  const pageId = findPageElementId(root, elementRecord);
  if (!pageId) return spec;
  const pageEl = elementRecord[pageId];
  if (!isSpecElement(pageEl)) return spec;

  const existingProps =
    isSpecElement(pageEl.props) || typeof pageEl.props === 'object'
      ? { ...(pageEl.props as Record<string, unknown>) }
      : {};

  const nextProps: Record<string, unknown> = { ...existingProps };
  if (pageSetup.size !== undefined) nextProps.size = pageSetup.size;
  if (pageSetup.orientation !== undefined)
    nextProps.orientation = pageSetup.orientation;
  for (const key of [
    'marginTop',
    'marginBottom',
    'marginLeft',
    'marginRight',
  ] as const) {
    if (pageSetup[key] !== undefined) nextProps[key] = pageSetup[key];
  }

  return {
    ...spec,
    elements: {
      ...elementRecord,
      [pageId]: { ...pageEl, props: nextProps },
    },
  };
}

export function stringifyJsonSpec(
  spec: Record<string, unknown> | null | undefined,
): string {
  if (!spec || Object.keys(spec).length === 0) return EMPTY_PDF_JSON_SPEC;
  return JSON.stringify(spec, null, 2);
}
